//#region react import
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
//#endregion

//#region functions import
import { getTrainingImagePath } from "../../../Helper/GetMachineSource";
import { FORMAT_TIMESTRING, formatSecondToStringTime } from "../../../Helper/TimeConverter";
import { getTranslations } from "../../../Helper/TranslationController";
//#endregion

//#region services import
import { downloadPdfFile, isPdfFileAvailable } from "../../../Helper/FileHelper";
//#endregion

//#region constants import
import { LOG } from "../../../Components/Documentation/Documentation.constants";
import { DOCUMENTATION } from "../../../Constants/Documentation";
import {
  ICON_FLAG,
  ICON_PDF,
  ICON_STAR_FILL_ON,
  ICON_TIMER,
  ICON_TRAFIC_LIGHT,
} from "../../../Constants/IconConstants";
import { TRANSLATIONS } from "../../../Constants/Translations";
//#endregion

//#region style import
import "./TrainingDetailGlobalInfo.style.scss";
//#endregion

const TrainingDetailGlobalInfo = ({ training, skills }) => {
  //#region useSelector
  const translations = useSelector((state) => state.translationSlice.translations);
  const translationsDictionariesToLoad = useSelector((state) => state.translationSlice.translationsDictionariesToLoad);
  const language = useSelector((state) => state.translationSlice.language);
  const activeCenterId = useSelector((state) => state.connexionSlice.trainer?.activeCenter.id);
  //#endregion

  //#region others use...
  const dispatch = useDispatch();
  //#endregion

  //#region useState
  const [image, setImage] = useState();
  const [isSimulationContentFile, setSimulationContentFile] = useState(false);
  const [isTrainingCatalogFile, setTrainingCatalogFile] = useState(false);
  //#endregion

  //#region constants
  const checkFileExist = useCallback(
    async (filePath, setFileExist) => {
      if (await isPdfFileAvailable(filePath, language)) {
        setFileExist(filePath);
      }
    },
    [language]
  );

  //#endregion

  //#region useEffect
  useEffect(() => {
    checkFileExist(
      DOCUMENTATION.SIMULATION_CONTENT(training.softwareCode, training.machineCode),
      setSimulationContentFile
    );
    checkFileExist(DOCUMENTATION.TRAINING_CATALOG(training.softwareCode, training.machineCode), setTrainingCatalogFile);
  }, [training.softwareCode, training.machineCode, checkFileExist]);

  useEffect(() => {
    const getImage = async () => {
      const imageGetted = await getTrainingImagePath(
        training.softwareCode,
        training.pictureFileName,
        training.machineCode,
        translationsDictionariesToLoad
      );
      setImage(imageGetted);
    };
    getImage();
  }, [translationsDictionariesToLoad, training]);
  //#endregion

  return (
    <div className="global-info">
      <div className="global-info-header">
        <div className="global-info-header-left">
          <div>
            <h2 className="title2">{getTranslations(training.trainingName, translations)}</h2>
            {training.isArchivedByCusForCenter && (
              <span>{getTranslations(TRANSLATIONS.TRAINING_ARCHIVED, translations)}</span>
            )}
          </div>
        </div>
        <div className="global-info-header-info">
          <div className="text-icon">
            <i className={ICON_TIMER} />
            <span>{formatSecondToStringTime(training.duration, translations, FORMAT_TIMESTRING.HHMM)}</span>
          </div>
          <div className="text-icon">
            <i className={ICON_FLAG} />
            <div>{`${training.stepCount} ${getTranslations("training_layout_detail_step", translations)}`}</div>
          </div>
          <div className="text-icon">
            <i className={ICON_TRAFIC_LIGHT} />
            <span>
              {`${training.exerciceCount}
              ${getTranslations("training_layout_detail_exercice", translations)}`}
            </span>
          </div>
          <span className="global-info-header-machine text-icon-machine">
            <i className={`icon-${training.machineCode}`} />
            <span>{getTranslations(training.machineName, translations)}</span>
          </span>
        </div>
      </div>
      <p />
      <div className="global-info-description">
        <img
          src={image}
          alt={image}
        />
        <div className="global-info-description-text">
          <span>{getTranslations(training.description, translations)}</span>
          <div className="pdfs-links">
            {isTrainingCatalogFile && (
              <div
                className="text-icon"
                onClick={() =>
                  downloadPdfFile(
                    DOCUMENTATION.SIMULATION_CONTENT(training.softwareCode, training.machineCode),
                    language,
                    getTranslations(TRANSLATIONS.SIMULATION_CONTENT, translations),
                    getTranslations(TRANSLATIONS.NOTIFICATION_FILE_DOWNLOADED, translations),
                    dispatch,
                    LOG.NOTICE_SIMULATION,
                    activeCenterId
                  )
                }
              >
                <i className={ICON_PDF} />
                <span>{getTranslations(TRANSLATIONS.SIMULATION_CONTENT, translations)}</span>
              </div>
            )}
            {isSimulationContentFile && (
              <div
                className="text-icon"
                onClick={() =>
                  downloadPdfFile(
                    DOCUMENTATION.TRAINING_CATALOG(training.softwareCode, training.machineCode),
                    language,
                    getTranslations(TRANSLATIONS.TRAINING_CATALOG, translations),
                    getTranslations(TRANSLATIONS.NOTIFICATION_FILE_DOWNLOADED, translations),
                    dispatch,
                    LOG.NOTICE_SIMULATION,
                    activeCenterId
                  )
                }
              >
                <i className={ICON_PDF} />
                <span>{getTranslations(TRANSLATIONS.TRAINING_LAYOUT_TRAINING_CATALOG, translations)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <h3 className="title3">{getTranslations("training_layout_detail_training_skills", translations)}</h3>
      <div className="global-info-skills">
        {skills?.map((skill) => (
          <div
            className="text-icon"
            key={skill}
          >
            <i className={ICON_STAR_FILL_ON} />
            <span>{getTranslations(skill, translations)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrainingDetailGlobalInfo;
